import { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { TrendingUp, Target, Trophy, Building2, Loader2 } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR");

export default function Dashboard() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/records")
      .then((r) => setRecords(r.data))
      .catch((e) => toast.error(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    let beklenen = 0,
      gerceklesen = 0,
      kazanilan = 0;
    const perIl = {};
    records.forEach((r) => {
      const v = r.values || {};
      const b = parseFloat(v.beklenen_ciro) || 0;
      const g = parseFloat(v.gerceklesen_ciro) || 0;
      beklenen += b;
      gerceklesen += g;
      if ((v.sonuc || "").toLowerCase().includes("kazan")) kazanilan += 1;
      const raw = (v.il || "").trim() || "Belirtilmemiş";
      const key = raw
        .toLocaleLowerCase("tr-TR")
        .replace(/ı/g, "i")
        .replace(/i̇/g, "i");
      if (!perIl[key]) perIl[key] = { il: raw, beklenen: 0, gerceklesen: 0 };
      perIl[key].beklenen += b;
      perIl[key].gerceklesen += g;
    });
    const ilList = Object.values(perIl)
      .sort((a, b) => b.gerceklesen + b.beklenen - (a.gerceklesen + a.beklenen))
      .slice(0, 12);
    return { beklenen, gerceklesen, kazanilan, ilList };
  }, [records]);

  const cards = [
    { label: "Toplam Beklenen Ciro", value: `₺${fmt(stats.beklenen)}`, icon: Target, color: "text-blue-500" },
    { label: "Toplam Gerçekleşen Ciro", value: `₺${fmt(stats.gerceklesen)}`, icon: TrendingUp, color: "text-emerald-500" },
    { label: "Kazanılan Müşteri", value: fmt(stats.kazanilan), icon: Trophy, color: "text-amber-500" },
    { label: "Toplam Kayıt", value: fmt(records.length), icon: Building2, color: "text-violet-500" },
  ];

  if (loading)
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  return (
    <div className="flex-1 overflow-auto grid-scroll p-4 md:p-6 space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">İl Ciro Özet</h1>
        <p className="text-sm text-muted-foreground">İl bazlı ciro dağılımı ve genel performans.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-5" data-testid={`stat-${c.label}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{c.label}</span>
              <c.icon className={`w-5 h-5 ${c.color}`} />
            </div>
            <div className="mt-3 font-display text-2xl font-bold">{c.value}</div>
          </Card>
        ))}
      </div>

      <Card className="p-5">
        <h2 className="font-display text-lg font-semibold mb-4">İl Bazlı Ciro Dağılımı</h2>
        {stats.ilList.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Veri yok.</p>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={stats.ilList}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="il" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  color: "hsl(var(--foreground))",
                }}
                formatter={(v) => `₺${fmt(v)}`}
              />
              <Bar dataKey="beklenen" name="Beklenen" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="gerceklesen" name="Gerçekleşen" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="font-display text-lg font-semibold mb-4">İl Sıralaması</h2>
        <div className="space-y-2">
          {stats.ilList.map((row, i) => (
            <div
              key={row.il}
              className="flex items-center justify-between py-2 border-b border-border last:border-0"
              data-testid={`il-row-${i}`}
            >
              <span className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-md bg-secondary grid place-items-center text-xs font-semibold">
                  {i + 1}
                </span>
                <span className="font-medium">{row.il}</span>
              </span>
              <span className="flex gap-6 text-sm">
                <span className="text-muted-foreground">Beklenen: ₺{fmt(row.beklenen)}</span>
                <span className="text-emerald-500 font-medium">Gerçekleşen: ₺{fmt(row.gerceklesen)}</span>
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
